import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  registerUser,
  rolesUser,
  departmentsUser,
  fetchUsers,
  updateUser,
  deleteUser,
} from "../services/api";
import { setAlert } from "../store/slices/alertSlice";
import { Modal, Pagination } from "react-bootstrap";

const RegisterForm = () => {
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    role: "",
    department: "",
  });
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [editingUserId, setEditingUserId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [rolesResponse, departmentsResponse, usersResponse] =
          await Promise.all([rolesUser(), departmentsUser(), fetchUsers(1)]);
        setRoles(rolesResponse.data);
        setDepartments(departmentsResponse.data);
        setUsers(usersResponse.data.users);
        setTotalPages(usersResponse.data.totalPages);
      } catch (error) {
        dispatch(setAlert({ message: "Error fetching data", type: "danger" }));
      }
    };
    fetchInitialData();
  }, [dispatch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const validateForm = () => {
    if (!formData.username) return "Username is required.";
    if (!formData.email) return "Email is required.";
    if (!editingUserId && !formData.password) return "Password is required.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      dispatch(setAlert({ message: validationError, type: "danger" }));
      return;
    }
  
    try {
      const userData = { ...formData };
  
      // If editing and password is empty, remove password from the data to prevent updating it
      if (!formData.password && editingUserId) {
        delete userData.password;
      }
  
      if (editingUserId) {
        await updateUser(editingUserId, userData); // Pass userData (without password if empty)
        dispatch(setAlert({ message: "User updated successfully", type: "success" }));
      } else {
        await registerUser(userData); // Register new user
        dispatch(setAlert({ message: "User registered successfully", type: "success" }));
      }
      resetForm();
      loadUsers(currentPage);
    } catch (error) {
      const errorMessage = "Error saving user";
      dispatch(setAlert({ message: errorMessage, type: "danger" }));
    }
  };
  

  const resetForm = () => {
    setFormData({ username: "", email: "", password: "", role: "", department: "" });
    setEditingUserId(null);
  };

  const handleEdit = (user) => {
    setFormData({
      username: user.username,
      email: user.email,
      password: "",
      role: user.role,
      department: user.department,
    });
    setEditingUserId(user._id);
  };

  const handleDelete = async () => {
    try {
      await deleteUser(deleteUserId);
      dispatch(setAlert({ message: "User deleted successfully", type: "success" }));
      setShowDeleteModal(false);
      loadUsers(currentPage);
    } catch (error) {
      dispatch(setAlert({ message: "Error deleting user", type: "danger" }));
    }
  };

  const loadUsers = async (page) => {
    setLoading(true);
    try {
      const response = await fetchUsers(page);
      setUsers(response.data.users);
      setTotalPages(response.data.totalPages);
      setCurrentPage(page);
    } catch (error) {
      dispatch(setAlert({ message: "Error loading users", type: "danger" }));
    }
    setLoading(false);
  };

  return (
    <div className="container mt-5">
      <div className="row">
        {/* Form Section */}
        <div className="col-lg-4 mb-4">
          <div className="card shadow-sm p-4">
            <h4>{editingUserId ? "Edit User" : "Register User"}</h4>
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="form-label">Username</label>
                <input
                  type="text"
                  className="form-control"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-control"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Password</label>
                <input
                  type="password"
                  className="form-control"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required={!editingUserId}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Role</label>
                <select
                  className="form-select"
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  required
                >
                  <option value="" disabled>
                    Select Role
                  </option>
                  {roles.map((role) => (
                    <option key={role._id} value={role._id}>
                      {role.roleName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">Department</label>
                <select
                  className="form-select"
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                  required
                >
                  <option value="" disabled>
                    Select Department
                  </option>
                  {departments.map((department) => (
                    <option key={department._id} value={department._id}>
                      {department.departmentName} ({department.departmentCode})
                    </option>
                  ))}
                </select>
              </div>
              <button type="submit" className="btn btn-primary w-100">
                {editingUserId ? "Update User" : "Register"}
              </button>
            </form>
          </div>
        </div>

        {/* Grid Section */}
        <div className="col-lg-8">
          <div className="card shadow-sm">
            <div className="card-header">
              <h4>User List</h4>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Username</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Department</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user._id}>
                        <td>{user.username}</td>
                        <td>{user.email}</td>
                        <td>{user.role.roleName}</td>
                        <td>{user.department ? user.department.departmentName : ''}</td>
                        <td>
                          <button
                            className="btn btn-warning btn-sm me-2"
                            title="Edit User"
                            onClick={() => handleEdit(user)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-danger btn-sm"
                            title="Delete User"
                            onClick={() => {
                              setDeleteUserId(user._id);
                              setShowDeleteModal(true);
                            }}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <Pagination>
                {[...Array(totalPages)].map((_, i) => (
                  <Pagination.Item
                    key={i}
                    active={i + 1 === currentPage}
                    onClick={() => loadUsers(i + 1)}
                  >
                    {i + 1}
                  </Pagination.Item>
                ))}
              </Pagination>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this user?</Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </button>
          <button className="btn btn-danger" onClick={handleDelete}>
            Delete
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default RegisterForm;
